import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useLabPreferences } from './preferences';
import { groupLabs } from './MaternalLabs';
import { usePermissions } from '@/features/permissions';
import { type LabResult, useMaternalLabs } from './api';

export function labTrendPoints(rows: LabResult[]) {
  return rows
    .flatMap((row) => {
      const date = row.collected_date || row.reported_date;
      if (!date || !/^-?\d+(?:\.\d+)?$/.test(row.value)) return [];
      const value = Number(row.value);
      return Number.isFinite(value) ? [{ date, value, id: row.id, timestamp: Date.parse(`${date}T00:00:00Z`) }] : [];
    })
    .sort((a, b) => a.timestamp - b.timestamp);
}

export function LabTrends({ pregnancyId = '', patientId }: { pregnancyId?: string; patientId?: string }) {
  const { can } = usePermissions();
  const { graphed } = useLabPreferences();
  const query = useMaternalLabs(pregnancyId, can('read_patient'), patientId);
  if (!can('read_patient')) return null;
  if (query.isError) return <p role="alert">Laboratory trends could not be loaded.</p>;
  if (query.isLoading) return <p>Loading laboratory trends…</p>;
  const groups = groupLabs(query.data?.results || []).groups;
  return (
    <section className="flex flex-col gap-4" aria-label="Laboratory value trends">
      <h3>Laboratory value trends</h3>
      <p>
        Grouped by test, units, specimen and method. Text results, inequalities, and undated results remain in View
        flowsheet.
      </p>
      {!groups.length && <p>No laboratory results imported yet.</p>}
      {!!groups.length && !groups.some(([key]) => graphed.includes(key)) && (
        <p>Select graph icons beside tests in View flowsheet to show their trends.</p>
      )}
      {groups
        .filter(([key]) => graphed.includes(key))
        .map(([key, rows]) => {
          const points = labTrendPoints(rows);
          if (!points.length) return <p key={key}>{rows[0].test_name}: no dated numeric values available to graph.</p>;
          return (
            <section key={key} className="min-w-0">
              <h4>
                {rows[0].test_name} ({rows[0].unit || 'no unit stated'})
              </h4>
              <div role="img" className="h-56" aria-label={`${rows[0].test_name} by test date`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={points} margin={{ left: 15, right: 25, bottom: 15 }}>
                    <CartesianGrid stroke="var(--border)" />
                    <XAxis
                      type="number"
                      dataKey="timestamp"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(v) => new Date(v).toISOString().slice(0, 10)}
                    />
                    <YAxis domain={['auto', 'auto']} />
                    <Tooltip labelFormatter={(v) => new Date(Number(v)).toISOString().slice(0, 10)} />
                    <Line dot type="linear" dataKey="value" stroke="var(--primary)" isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <details>
                <summary>Values</summary>
                {points.map((point) => (
                  <p key={point.id}>
                    {point.date}: {point.value} {rows[0].unit}
                  </p>
                ))}
              </details>
            </section>
          );
        })}
    </section>
  );
}
